import React, { useContext, useState, useEffect, useRef } from 'react'
import { EPCRContext } from '../EPCR'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import FormHelperText from '@mui/material/FormHelperText'
import Stack from '@mui/material/Stack'
import AnDateTimePicker from '../../../../components/DateTime/DateTimePicker'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import API from '../../../../services/API'
import LinkedEvent from './Event'
import OccurredAtEvent from './OccurredAtEvent'
import IncidentDate from './Date'
import IncidentLocation from './Location'
import IncidentType from './Type'
import Reported from './Reported'
import StaffInvolved from './StaffInvolved'
import VehiclesInvolved from './VehiclesInvolved'
import EPCRCard from '../../../../components/Cards/EPCRCard'
import moment from 'moment'
import IncidentNumber from './IncidentNumber'
import ErrorModal from '../../../../components/Modals/Error'
import { isFullLayoutMode } from '../formTypes'

const Details = () => {
  const {
    formValues,
    handleChange,
    setDownloadState,
    setFormValues,
    setUploadState,
    setIsDraftDirty,
    formID,
  } = useContext(EPCRContext)
  const [eventOptions, setEventOptions] = useState([])
  const [staffOptions, setStaffOptions] = useState([])
  const [vehicleOptions, setVehicleOptions] = useState([])
  const [cadLookupState, setCadLookupState] = useState(
    formValues?.incident?.cadIncident ? 'success' : 'idle',
  )
  const [cadLookupError, setCadLookupError] = useState(null)
  const [pendingCad, setPendingCad] = useState(null)
  const lastLinkedCadNumber = useRef(
    formValues?.incident?.cadIncident
      ? String(formValues?.incident?.incidentNumber || '').replace(/\D/g, '')
      : null,
  )
  // A CAD number the user has explicitly declined in the confirm popup —
  // don't re-open the popup for it until the number changes.
  const declinedCadNumber = useRef(null)

  useEffect(() => {
    if (formValues?.incident?.incidentType !== 'Event') {
      setEventOptions([])
      return
    }

    const existingEvent =
      formValues?.incident?.linkedEvent?._id ||
      formValues?.incident?.linkedEvent

    setDownloadState('pending')
    API.post('/todays-events-list', {
      existingEvent,
      incidentDate: formValues?.incident?.incidentDate,
    })
      .then((res) => {
        console.log('res-epcr-incidents', res.data)
        if (res.status === 200) {
          setEventOptions(res.data)
          if (formValues?.incident?.linkedEvent?._id) {
            handleChange({
              moduleName: 'incident',
              field: 'linkedEvent',
              value: formValues?.incident?.linkedEvent?._id,
            })
          }
        }
      })
      .finally(() => setDownloadState('done'))
  }, [
    formValues?.incident?.incidentDate,
    formValues?.incident?.incidentType,
    formValues?.incident?.linkedEvent?._id,
    formValues?.incident?.linkedEvent,
  ])

  useEffect(() => {
    API.get('/vehicleList').then((res) => {
      if (res.status == 200) {
        setVehicleOptions(res.data)
        setDownloadState('done')
      }
    })
  }, [])

  useEffect(() => {
    setDownloadState('pending')
    API.get('/employee-search')
      .then((res) => {
        if (res.status === 200) {
          setStaffOptions(res.data)
        } else {
          setDownloadState('error')
        }
      })
      .catch((e) => setDownloadState('error'))
      .finally(() =>
        setDownloadState((prev) => (prev === 'error' ? 'error' : 'done')),
      )
  }, [])

  useEffect(() => {
    const cadNumber = String(formValues?.incident?.incidentNumber || '').replace(
      /\D/g,
      '',
    )

    const unlinkCad = () => {
      if (!formValues?.incident?.cadIncident) return
      lastLinkedCadNumber.current = null
      setFormValues((prev) => ({
        ...prev,
        incident: {
          ...(prev.incident || {}),
          cadIncident: null,
          linkedEvent: null,
        },
      }))
      setUploadState('pending')
      setIsDraftDirty(true)
    }

    if (cadNumber.length !== 10) {
      unlinkCad()
      setPendingCad(null)
      declinedCadNumber.current = null
      setCadLookupState('idle')
      return undefined
    }

    if (
      formValues?.incident?.cadIncident &&
      lastLinkedCadNumber.current === cadNumber
    ) {
      setCadLookupState('success')
      return undefined
    }

    if (declinedCadNumber.current === cadNumber) {
      setCadLookupState('idle')
      return undefined
    }

    setCadLookupState('loading')

    const timeout = setTimeout(() => {
      // API.js never rejects on HTTP errors (validateStatus: () => true), so
      // the status must be checked explicitly — a 404 previously fell into
      // the success path and silently wiped the field.
      API.get(`/epcr/cad-incident/${cadNumber}`)
        .then((res) => {
          if (res.status === 200 && res.data?.cadIncident) {
            // Don't link yet — ask the user to confirm the match first.
            setPendingCad({ ...res.data, cadNumber })
            setCadLookupState('found')
            return
          }

          unlinkCad()

          if (res.status === 404) {
            setCadLookupState('notFound')
            setCadLookupError({
              title: 'CAD incident not found',
              message: `No CAD incident matched ${formValues?.incident?.incidentNumber}. Check the number and try again.`,
            })
            return
          }

          setCadLookupState('error')
          setCadLookupError({
            title: 'Unable to check CAD',
            message:
              'The CAD incident could not be checked right now. Please try again.',
          })
        })
        .catch(() => {
          unlinkCad()
          setCadLookupState('error')
          setCadLookupError({
            title: 'Unable to check CAD',
            message:
              'The CAD incident could not be checked right now. Please try again.',
          })
        })
    }, 500)

    return () => clearTimeout(timeout)
  }, [
    formValues?.incident?.incidentNumber,
    formValues?.incident?.cadIncident,
    setFormValues,
    setIsDraftDirty,
    setUploadState,
  ])

  const confirmPendingCad = () => {
    if (!pendingCad) return
    const cad = pendingCad
    lastLinkedCadNumber.current = cad.cadNumber
    declinedCadNumber.current = null

    setFormValues((prev) => ({
      ...prev,
      incident: {
        ...(prev.incident || {}),
        cadIncident: cad.cadIncident,
        incidentNumber: cad.incidentNumber,
        incidentType: cad.incidentType,
        linkedEvent: cad.linkedEvent || prev.incident?.linkedEvent,
        incidentLocation: cad.incidentLocation,
        reportedBy: cad.reportedBy,
        reportedComplaint: cad.reportedComplaint,
      },
    }))
    setUploadState('pending')
    setIsDraftDirty(true)
    setCadLookupState('success')
    setPendingCad(null)
  }

  const declinePendingCad = () => {
    if (pendingCad) declinedCadNumber.current = pendingCad.cadNumber
    setPendingCad(null)
    setCadLookupState('idle')
    // If a different CAD was linked before the number was edited, drop the
    // stale link — the typed number no longer matches it.
    if (formValues?.incident?.cadIncident) {
      lastLinkedCadNumber.current = null
      setFormValues((prev) => ({
        ...prev,
        incident: {
          ...(prev.incident || {}),
          cadIncident: null,
          linkedEvent: null,
        },
      }))
      setUploadState('pending')
      setIsDraftDirty(true)
    }
  }

  return (
    <EPCRCard>
      <Typography variant="title2">Incident Details</Typography>
      <Grid container spacing={2}>
        <IncidentDate formValues={formValues} handleChange={handleChange} />

        <IncidentType formValues={formValues} handleChange={handleChange} />

        <LinkedEvent
          formValues={formValues}
          handleChange={handleChange}
          eventOptions={eventOptions}
        />

        <OccurredAtEvent formValues={formValues} handleChange={handleChange} />

        <IncidentNumber
          formValues={formValues}
          handleChange={handleChange}
          cadLookupState={cadLookupState}
        />
        {formValues?.incident?.cadIncident && cadLookupState === 'success' && (
          <Grid item xs={12}>
            <Alert severity="success">
              This ePCR is linked to CAD incident{' '}
              {formValues.incident.incidentNumber}. CAD-provided incident
              details are locked.
            </Alert>
          </Grid>
        )}
        <Grid item xs={12} lg={4}>
          <Box display="flex" flex={1}>
            <TextField
              fullWidth
              value={formValues?.incident?.incidentLocation ?? ''}
              disabled={!!formValues?.incident?.cadIncident}
              onChange={(e) =>
                handleChange({
                  moduleName: 'incident',
                  field: 'incidentLocation',
                  value: e.target.value,
                })
              }
              label="Incident Location"
              multiline
            />
          </Box>
        </Grid>
        <Reported formValues={formValues} />
        <StaffInvolved
          formValues={formValues}
          handleChange={handleChange}
          staffOptions={staffOptions.sort((a, b) => {
            const f = a?.userAccount?.firstName
            const l = a?.userAccount?.lastName
            return f < l ? -1 : f > l ? 1 : 0
          })}
        />
        {isFullLayoutMode(formValues.mode) && (
          <VehiclesInvolved
            formValues={formValues}
            handleChange={handleChange}
            vehicleOptions={vehicleOptions}
          />
        )}
      </Grid>
      <ErrorModal
        open={!!cadLookupError}
        title={cadLookupError?.title}
        message={cadLookupError?.message}
        noAutoClose
        onClose={() => setCadLookupError(null)}
      />
      <Dialog
        open={!!pendingCad}
        onClose={declinePendingCad}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>CAD Incident Found</DialogTitle>
        <DialogContent>
          <Stack gap={1.5}>
            <Typography variant="body2" color="text.secondary">
              Check these details match the incident you are recording before
              linking. Linking will lock the CAD-provided incident details.
            </Typography>
            <Stack gap={0.75}>
              {[
                ['Incident Number', pendingCad?.incidentNumber],
                [
                  'Control Room',
                  pendingCad?.cad?.controlRoomTitle ||
                    (pendingCad?.linkedEvent?.title
                      ? `${pendingCad.linkedEvent.title} — Event Control`
                      : null),
                ],
                ['Event', pendingCad?.linkedEvent?.title],
                [
                  'Created',
                  pendingCad?.cad?.createdAt
                    ? moment(pendingCad.cad.createdAt).format(
                        'DD/MM/YYYY HH:mm',
                      )
                    : null,
                ],
                ['Type', pendingCad?.cad?.incidentType],
                ['Priority', pendingCad?.cad?.priority],
                [
                  'Complaint',
                  pendingCad?.cad?.presentingComplaint ||
                    pendingCad?.reportedComplaint,
                ],
                ['Location', pendingCad?.incidentLocation],
                ['Reported By', pendingCad?.reportedBy],
              ]
                .filter(([, value]) => value)
                .map(([label, value]) => (
                  <Stack key={label} direction="row" gap={1}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 120, flexShrink: 0 }}
                    >
                      {label}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {value}
                    </Typography>
                  </Stack>
                ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={declinePendingCad} color="inherit">
            Change Number
          </Button>
          <Button onClick={confirmPendingCad} variant="contained">
            Link CAD Incident
          </Button>
        </DialogActions>
      </Dialog>
    </EPCRCard>
  )
}

export default Details
